import type { MatchState } from '../../shared/types';
import type { ProgressionSelectionState, UpgradeCard } from '../../shared/progression/progressionTypes';
import type { RewardTimelineSnapshot } from './rewardRevealDirector';
import { RewardFxLayer } from './rewardFxLayer';
import { buildRewardReelSymbols, rewardReelFrame, type RewardReelSymbol } from './rewardReelAnimator';
import { formatUpgradeEffectValue, statPresentationMetadata } from '../../shared/presentation/statPresentation';
import { localization } from '../localization/localizationService';
import type { LocalizationService } from '../localization/localizationTypes';
import { upgradeKey } from '../localization/contentKeys';

export type ProgressionRole = 'driver' | 'gunner' | 'single';

export interface RewardRevealViewCallbacks {
  chooseUpgrade(index: number): void;
  continueRelic(): void;
  relicInfo?(relicId: string): { label: string; description: string; iconId?: string; iconUrl?: string | null } | null;
}

export class RewardRevealView {
  readonly root: HTMLElement;
  readonly selectionHost: HTMLElement;
  readonly relicHost: HTMLElement;
  readonly debugHost: HTMLElement;
  readonly fx: RewardFxLayer;
  private cardButtons: HTMLButtonElement[] = [];
  private cardReels: HTMLElement[] = [];
  private cardSymbols: RewardReelSymbol[][] = [];
  private lastCardCellIndices: number[] = [];
  private selectionTitle: HTMLElement | null = null;
  private fuse: HTMLElement | null = null;
  private peerStatus: HTMLElement | null = null;
  private continuePrompt: HTMLButtonElement | null = null;
  private relicReel: HTMLElement | null = null;
  private relicPlate: HTMLElement | null = null;
  private relicOutline: HTMLElement | null = null;
  private relicSymbols: RewardReelSymbol[] = [];
  private lastRelicCellIndex = -1;
  private lastLockedCount = 0;
  private rewardIdentity = '';
  private currentSelection: ProgressionSelectionState | null = null;
  private currentRole: ProgressionRole = 'single';
  private currentTimeline: RewardTimelineSnapshot | null = null;
  private readonly unsubscribeLocalization: () => void;

  constructor(
    container: HTMLElement,
    private readonly cb: RewardRevealViewCallbacks,
    private readonly i18n: LocalizationService = localization,
  ) {
    const reducedMotion = typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reducedFlash = reducedMotion || new URLSearchParams(globalThis.location?.search ?? '').has('reducedFlash');
    this.fx = new RewardFxLayer(reducedFlash);
    this.root = document.createElement('div');
    this.root.id = 'progression-overlay';
    this.root.className = 'reward-overlay';
    this.root.classList.toggle('reward-overlay--reduced-flash', reducedFlash);
    this.root.hidden = true;
    const scrim = element('div', 'reward-scrim');
    this.selectionHost = document.createElement('section');
    this.selectionHost.id = 'progression-selection-layer';
    this.selectionHost.className = 'reward-layer reward-layer--upgrade';
    this.selectionHost.hidden = true;
    this.relicHost = document.createElement('section');
    this.relicHost.id = 'progression-relic-layer';
    this.relicHost.className = 'reward-layer reward-layer--relic';
    this.relicHost.hidden = true;
    this.relicHost.addEventListener('click', (event) => {
      if (!(event.target instanceof HTMLButtonElement)) this.cb.continueRelic();
    });
    this.debugHost = document.createElement('pre');
    this.debugHost.id = 'progression-debug-layer';
    this.debugHost.className = 'reward-debug';
    this.debugHost.hidden = !new URLSearchParams(globalThis.location?.search ?? '').has('progressionDebug');
    this.root.append(scrim, this.fx.element, this.selectionHost, this.relicHost);
    container.append(this.root, this.debugHost);
    this.unsubscribeLocalization = this.i18n.subscribe(() => this.refreshLocalizedContent());
  }

  renderUpgrade(selection: ProgressionSelectionState, role: ProgressionRole): void {
    this.currentSelection = selection;
    this.currentRole = role;
    const offer = selection.singlePlayerOffer ?? (role === 'driver' ? selection.driverOffer : selection.gunnerOffer) ?? [];
    this.selectionHost.replaceChildren();
    this.cardButtons = [];
    this.cardReels = [];
    this.cardSymbols = [];
    this.lastCardCellIndices = [];
    this.lastLockedCount = 0;
    this.rewardIdentity = selection.offerId;
    const stage = element('div', 'reward-stage reward-stage--upgrade');
    const kicker = element('div', 'reward-kicker', this.i18n.t('ui.progression.available'));
    this.selectionTitle = element('h2', 'reward-title', this.i18n.t('ui.progression.levelUp'));
    const level = element('div', 'reward-level-number', this.i18n.t('ui.progression.level', { level: selection.level }));
    const bank = element('div', 'reward-card-bank');
    offer.forEach((card, index) => bank.appendChild(this.buildCard(card, index, selection.offerId)));
    this.peerStatus = role === 'single' ? null : element('div', 'reward-peer-status');
    this.fuse = element('div', 'reward-auto-fuse');
    this.fuse.dataset['progressionTimer'] = 'true';
    stage.append(kicker, this.selectionTitle, level, bank);
    if (this.peerStatus) stage.appendChild(this.peerStatus);
    stage.appendChild(this.fuse);
    this.selectionHost.appendChild(stage);
  }

  private buildCard(card: UpgradeCard, index: number, identity: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'reward-card';
    button.dataset['resultRarity'] = card.rarity;
    button.dataset['index'] = String(index);
    const name = this.i18n.t(upgradeKey(card.categoryId), undefined, card.categoryId);
    const rarity = this.i18n.t(`ui.rarity.${card.rarity}`, undefined, card.rarity);
    button.setAttribute('aria-label', `${index + 1}: ${name}, ${rarity}`);
    const hotkey = element('span', 'reward-card__hotkey', String(index + 1));
    const reelWindow = element('div', 'reward-card__reel-window');
    reelWindow.setAttribute('aria-hidden', 'true');
    const reelTrack = element('div', 'reward-card__reel-track');
    const symbols = buildRewardReelSymbols(identity, index, 'upgrade');
    this.cardSymbols.push(symbols);
    this.lastCardCellIndices.push(-1);
    button.dataset['rarity'] = symbols[0]?.rarity ?? 'neutral';
    for (const symbol of symbols) {
      const cell = element('div', 'reward-card__symbol');
      cell.dataset['rarity'] = symbol.rarity;
      cell.append(element('span', 'reward-card__symbol-glyph', symbol.glyph), element('span', '', this.reelLabel(symbol.label)));
      reelTrack.appendChild(cell);
    }
    const finalSymbol = element('div', 'reward-card__lock-symbol');
    finalSymbol.append(element('span', 'reward-card__symbol-glyph', glyphFor(card.categoryId)), element('span', '', this.i18n.t('ui.progression.locked')));
    reelWindow.append(reelTrack, finalSymbol);
    this.cardReels.push(reelTrack);
    const content = element('div', 'reward-card__content');
    content.append(
      element('div', 'reward-card__rarity', rarity),
      element('div', 'reward-card__name', name),
      buildEffects(card, this.i18n),
      element('div', 'reward-card__focus-rail', this.i18n.t('ui.progression.select')),
    );
    button.append(hotkey, reelWindow, content);
    button.addEventListener('mouseenter', () => this.focusCard(index));
    button.addEventListener('focus', () => this.focusCard(index));
    button.addEventListener('click', () => this.cb.chooseUpgrade(index));
    this.cardButtons.push(button);
    return button;
  }

  updateUpgrade(selection: ProgressionSelectionState, role: ProgressionRole, timeline: RewardTimelineSnapshot, nowMs: number): void {
    this.currentSelection = selection;
    this.currentRole = role;
    this.currentTimeline = timeline;
    this.selectionHost.dataset['phase'] = timeline.state;
    this.selectionHost.hidden = false;
    this.relicHost.hidden = true;
    for (let index = 0; index < this.cardButtons.length; index++) {
      const locked = index < timeline.lockedCards;
      const card = this.cardButtons[index]!;
      card.classList.toggle('reward-card--locked', locked);
      const frame = rewardReelFrame(timeline.elapsedMs, index, 'upgrade');
      this.cardReels[index]?.style.setProperty('--reward-reel-y', `${frame.translateY}px`);
      this.cardReels[index]?.style.setProperty('--reward-reel-velocity', String(frame.velocity));
      if (locked) {
        card.dataset['rarity'] = card.dataset['resultRarity'] ?? 'common';
      } else if (frame.visibleCellIndex !== this.lastCardCellIndices[index]) {
        card.dataset['rarity'] = this.cardSymbols[index]?.[frame.visibleCellIndex]?.rarity ?? 'neutral';
        this.lastCardCellIndices[index] = frame.visibleCellIndex;
      }
    }
    for (let index = this.lastLockedCount; index < timeline.lockedCards; index++) {
      const card = this.cardButtons[index];
      if (!card) continue;
      restartClass(card, 'reward-card--lock-hit');
      const rarity = card.dataset['rarity'] ?? 'common';
      this.fx.burst(rarity, shardCount(rarity), `${this.rewardIdentity}:card:${index}`, card, index === 2 ? 'final-card' : 'card');
    }
    this.lastLockedCount = timeline.lockedCards;
    const selected = localSelection(selection, role);
    if (selected !== undefined) {
      this.selectionTitle!.textContent = this.i18n.t('ui.progression.lockedIn');
      this.markSelected(selected);
    } else {
      this.selectionTitle!.textContent = this.i18n.t('ui.progression.levelUp');
    }
    if (this.fuse && selection.expiresAtWallMs !== undefined) {
      const remaining = Math.max(0, selection.expiresAtWallMs - nowMs);
      const total = Math.max(1, selection.expiresAtWallMs - (selection.offerStartedAtWallMs ?? nowMs));
      this.fuse.style.setProperty('--reward-fuse', String(remaining / total));
      this.fuse.textContent = remaining <= 3_000 ? this.i18n.t('ui.progression.auto', { seconds: Math.ceil(remaining / 1_000) }) : '';
    }
    if (this.peerStatus) {
      const me = role === 'driver' ? selection.driverSelection : selection.gunnerSelection;
      const peerRole = this.i18n.t(role === 'driver' ? 'ui.role.gunner' : 'ui.role.driver');
      const peer = role === 'driver' ? selection.gunnerSelection : selection.driverSelection;
      this.peerStatus.replaceChildren(
        statusLine(this.i18n.t('ui.role.you'), me !== undefined ? this.i18n.t('ui.status.ready') : this.i18n.t('ui.progression.choosing')),
        statusLine(peerRole, peer !== undefined ? this.i18n.t('ui.status.ready') : this.i18n.t('ui.progression.choosing')),
      );
      if (me !== undefined && peer !== undefined) this.peerStatus.appendChild(element('strong', 'reward-crew-ready', this.i18n.t('ui.progression.crewReady')));
    }
  }

  renderRelic(selection: ProgressionSelectionState, role: ProgressionRole): void {
    this.currentSelection = selection;
    this.currentRole = role;
    const result = selection.relicResult!;
    const info = this.cb.relicInfo?.(result.relicId) ?? null;
    this.rewardIdentity = `relic:${result.acquisitionSequence}`;
    this.relicHost.replaceChildren();
    this.relicHost.removeAttribute('data-rarity');
    const stage = element('div', 'reward-stage reward-stage--relic');
    const signal = element('div', 'reward-kicker reward-relic__signal', this.i18n.t('ui.progression.relicSignal'));
    this.relicPlate = element('article', 'reward-relic');
    this.relicSymbols = buildRewardReelSymbols(this.rewardIdentity, 0, 'relic');
    this.lastRelicCellIndex = -1;
    this.relicPlate.dataset['rarity'] = this.relicSymbols[0]?.rarity ?? 'neutral';
    this.relicHost.dataset['rarity'] = this.relicPlate.dataset['rarity'];
    this.relicOutline = element('div', 'reward-relic__roulette-outline');
    const reelWindow = element('div', 'reward-relic__reel-window');
    this.relicReel = element('div', 'reward-relic__reel-track');
    for (const symbol of this.relicSymbols) {
      const cell = element('div', 'reward-relic__symbol');
      cell.dataset['rarity'] = symbol.rarity;
      cell.append(element('span', 'reward-relic__symbol-glyph', symbol.glyph), element('span', '', this.reelLabel(symbol.label)));
      this.relicReel.appendChild(cell);
    }
    reelWindow.appendChild(this.relicReel);
    const final = element('div', 'reward-relic__final');
    const icon = element('div', 'reward-relic__icon');
    if (info?.iconUrl) {
      const image = document.createElement('img');
      image.src = info.iconUrl;
      image.alt = '';
      icon.appendChild(image);
    } else {
      icon.textContent = 'RC';
      icon.classList.add('reward-relic__icon--fallback');
    }
    final.append(
      element('div', 'reward-relic__rarity', this.i18n.t(`ui.rarity.${result.rarity}`, undefined, result.rarity)),
      icon,
      element('h2', 'reward-relic__name', info?.label ?? this.i18n.t('ui.progression.unidentifiedRelic')),
      element('p', 'reward-relic__description', info?.description ?? ''),
      element('div', `reward-relic__stack${result.stackCountAfter > 1 ? ' reward-relic__stack--up' : ''}`, result.stackCountAfter > 1
        ? this.i18n.t('ui.progression.stackUp', { before: result.stackCountAfter - 1, after: result.stackCountAfter })
        : this.i18n.t('ui.progression.stack', { count: result.stackCountAfter })),
    );
    this.continuePrompt = document.createElement('button');
    this.continuePrompt.type = 'button';
    this.continuePrompt.className = 'reward-continue';
    this.continuePrompt.textContent = this.i18n.t('ui.progression.fastForward');
    this.continuePrompt.addEventListener('click', () => this.cb.continueRelic());
    this.relicPlate.append(this.relicOutline, reelWindow, final, this.continuePrompt);
    stage.append(signal, this.relicPlate);
    if (role !== 'single') stage.appendChild(element('div', 'reward-peer-status reward-peer-status--relic'));
    this.relicHost.appendChild(stage);
  }

  updateRelic(selection: ProgressionSelectionState, role: ProgressionRole, timeline: RewardTimelineSnapshot): void {
    this.currentSelection = selection;
    this.currentRole = role;
    this.currentTimeline = timeline;
    this.relicHost.dataset['phase'] = timeline.state;
    this.relicHost.hidden = false;
    this.selectionHost.hidden = true;
    const frame = rewardReelFrame(timeline.elapsedMs, 0, 'relic');
    this.relicReel?.style.setProperty('--reward-reel-y', `${frame.translateY}px`);
    this.relicReel?.style.setProperty('--reward-reel-velocity', String(frame.velocity));
    if (timeline.finalVisible) {
      const finalRarity = selection.relicResult?.rarity ?? 'common';
      this.relicPlate?.setAttribute('data-rarity', finalRarity);
      this.relicHost.dataset['rarity'] = finalRarity;
    } else if (frame.visibleCellIndex !== this.lastRelicCellIndex) {
      const spinningRarity = this.relicSymbols[frame.visibleCellIndex]?.rarity ?? 'neutral';
      this.relicPlate?.setAttribute('data-rarity', spinningRarity);
      this.relicHost.dataset['rarity'] = spinningRarity;
      if (this.relicOutline) restartClass(this.relicOutline, 'reward-relic__roulette-outline--flash');
      this.lastRelicCellIndex = frame.visibleCellIndex;
    }
    this.continuePrompt?.setAttribute('aria-disabled', String(!timeline.continueArmed));
    if (this.continuePrompt) this.continuePrompt.textContent = timeline.continueArmed
      ? this.i18n.t('ui.progression.continue')
      : this.i18n.t('ui.progression.fastForward');
    const peer = this.relicHost.querySelector<HTMLElement>('.reward-peer-status--relic');
    if (peer && role !== 'single') {
      const mine = role === 'driver' ? selection.driverRelicAcknowledged : selection.gunnerRelicAcknowledged;
      const theirs = role === 'driver' ? selection.gunnerRelicAcknowledged : selection.driverRelicAcknowledged;
      peer.replaceChildren(
        statusLine(this.i18n.t('ui.role.you'), mine ? this.i18n.t('ui.status.ready') : this.i18n.t('ui.progression.viewing')),
        statusLine(this.i18n.t('ui.progression.partner'), theirs ? this.i18n.t('ui.status.ready') : this.i18n.t('ui.progression.viewing')),
      );
      if (mine && theirs) peer.appendChild(element('strong', 'reward-crew-ready', this.i18n.t('ui.progression.crewReady')));
    }
  }

  startEntrance(identity: string): void {
    restartClass(this.root, 'reward-overlay--edge-impact');
    const host = this.root.dataset['kind'] === 'relic' ? this.relicHost : this.selectionHost;
    const stage = host.querySelector<HTMLElement>('.reward-stage');
    if (stage) restartClass(stage, 'reward-stage--enter');
    this.fx.enter(identity);
  }

  impactRelic(selection: ProgressionSelectionState): void {
    const rarity = selection.relicResult?.rarity ?? 'common';
    if (this.relicPlate) restartClass(this.relicPlate, 'reward-relic--lock-hit');
    this.fx.burst(rarity, shardCount(rarity, true), this.rewardIdentity, this.relicPlate ?? undefined, 'relic');
  }

  impactUpgradeSelection(index: number, rarity: string): void {
    const card = this.cardButtons[index];
    if (!card) return;
    this.fx.burst(rarity, shardCount(rarity) + 6, `${this.rewardIdentity}:manual:${index}`, card, 'final-card');
  }

  shake(intensity: number): void {
    this.root.style.setProperty('--reward-shake', `${Math.max(2, Math.round(intensity * 18))}px`);
    restartClass(this.root, 'reward-overlay--shake');
  }

  showUpgrade(timeline: RewardTimelineSnapshot): void {
    this.root.hidden = false;
    this.root.dataset['kind'] = 'upgrade';
    this.root.dataset['phase'] = timeline.state;
  }

  showRelic(timeline: RewardTimelineSnapshot): void {
    this.root.hidden = false;
    this.root.dataset['kind'] = 'relic';
    this.root.dataset['phase'] = timeline.state;
  }

  hide(): void {
    this.root.hidden = true;
    this.root.classList.remove('reward-overlay--edge-impact');
    this.root.classList.remove('reward-overlay--shake');
    this.selectionHost.hidden = true;
    this.relicHost.hidden = true;
    this.fx.clear();
  }

  focusCard(index: number): void {
    this.cardButtons.forEach((button, cardIndex) => button.classList.toggle('reward-card--focused', cardIndex === index));
  }

  markSelected(index: number): void {
    this.cardButtons.forEach((button, cardIndex) => {
      button.disabled = true;
      button.classList.toggle('reward-card--selected', cardIndex === index);
      button.classList.toggle('reward-card--rejected', cardIndex !== index);
    });
  }

  updateDebug(text: string): void {
    if (!this.debugHost.hidden) this.debugHost.textContent = text;
  }

  dispose(): void {
    this.unsubscribeLocalization();
    this.root.remove();
    this.debugHost.remove();
  }

  private reelLabel(label: string): string {
    return this.i18n.t(`ui.progression.reel.${label.toLowerCase()}`, undefined, label);
  }

  private refreshLocalizedContent(): void {
    const selection = this.currentSelection;
    if (!selection) return;
    if (selection.kind === 'upgrade') {
      this.renderUpgrade(selection, this.currentRole);
      if (this.currentTimeline) this.updateUpgrade(selection, this.currentRole, this.currentTimeline, Date.now());
    } else {
      this.renderRelic(selection, this.currentRole);
      if (this.currentTimeline) this.updateRelic(selection, this.currentRole, this.currentTimeline);
    }
  }
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}

function restartClass(node: HTMLElement, className: string): void {
  node.classList.remove(className);
  void node.offsetWidth;
  node.classList.add(className);
}

function statusLine(label: string, status: string): HTMLElement {
  const line = element('span', 'reward-peer-status__line');
  line.append(element('b', '', `${label} // `), document.createTextNode(status));
  return line;
}

function localSelection(selection: ProgressionSelectionState, role: ProgressionRole): number | undefined {
  return role === 'single'
    ? selection.singlePlayerSelection
    : role === 'driver'
      ? selection.driverSelection
      : selection.gunnerSelection;
}

function glyphFor(id: string): string {
  if (/damage|cannon|mg/i.test(id)) return '✦';
  if (/dash|speed|jump/i.test(id)) return '⬢';
  if (/integrity|armor|shield/i.test(id)) return '▣';
  return '◈';
}

function buildEffects(card: UpgradeCard, i18n: LocalizationService): HTMLDivElement {
  const list = element('div', 'reward-card__effect');
  const compact = card.rolledEffects.length > 1;
  list.classList.toggle('reward-card__effect--compact', compact);
  list.dataset['effectCount'] = String(card.rolledEffects.length);
  const translate = (key: string, params?: Record<string, string | number>, fallback?: string) => i18n.t(key, params, fallback);
  for (const effect of card.rolledEffects) {
    const row = element('div', 'reward-card__effect-row');
    row.append(
      element('span', 'reward-card__effect-label', statPresentationMetadata(effect.statId, translate).label),
      element('span', 'reward-card__effect-value', formatUpgradeEffectValue(effect)),
    );
    list.appendChild(row);
  }
  return list;
}

function shardCount(rarity: string, relic = false): number {
  const boost = relic ? 4 : 0;
  if (rarity === 'legendary') return 24 + boost;
  if (rarity === 'epic') return 16 + boost;
  if (rarity === 'rare') return 10 + boost;
  return 6 + boost;
}
